/**
 * Image compression utility using Canvas API
 * Compresses images before uploading to reduce file sizes
 */

class ImageCompressor {
  /**
   * Compress an image file
   * @param {File} file - The image file to compress
   * @param {Object} options - Compression options
   * @returns {Promise<File>} - Compressed file
   */
  static async compressImage(file, options = {}) {
    const {
      maxWidth = 2560,  // Increased for high-DPI displays
      maxHeight = 1920, // Increased for high-DPI displays
      quality = 0.92,   // Higher quality to reduce pixelation
      format = 'image/jpeg'
    } = options;

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        URL.revokeObjectURL(img.src);
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create new File object with original name
              const compressedFile = new File([blob], file.name, {
                type: format,
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Canvas to Blob conversion failed'));
            }
          },
          format,
          quality
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to load image'));
      };
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    });
  }

  /**
   * Check if compression would be beneficial
   * @param {File} file - The image file
   * @param {number} threshold - File size threshold in bytes (default 500KB)
   * @returns {boolean}
   */
  static shouldCompress(file, threshold = 512 * 1024) {
    return file.size > threshold;
  }

  /**
   * Get compression statistics
   * @param {File} originalFile 
   * @param {File} compressedFile 
   * @returns {Object}
   */
  static getCompressionStats(originalFile, compressedFile) {
    const originalSize = originalFile.size;
    const compressedSize = compressedFile.size;
    const savedBytes = originalSize - compressedSize;
    const compressionRatio = ((savedBytes / originalSize) * 100).toFixed(1);

    return {
      originalSize,
      compressedSize,
      savedBytes,
      compressionRatio: `${compressionRatio}%`,
      originalSizeMB: (originalSize / (1024 * 1024)).toFixed(2),
      compressedSizeMB: (compressedSize / (1024 * 1024)).toFixed(2)
    };
  }
}

export default ImageCompressor; 